import catalogSnapshot from './pricingCatalog.snapshot.json';

export type BillingInterval = 'monthly' | 'yearly';

export type PlanTier = 'free' | 'starter' | 'core' | 'growth' | 'scale';

export interface PlanLimits {
  sites: number | null;
  users: number;
  documentsPerMonth: number;
  complianceHub: boolean;
  brsrExports: boolean;
  greenFinance: boolean;
  carbonCredits: boolean;
}

export interface PublicFixedPlan {
  planId: string;
  name: string;
  description: string;
  amountInr: number;
  amountInrInclGst?: number;
  interval: BillingInterval;
  tier?: PlanTier;
  requiresPayment?: boolean;
  limits?: PlanLimits;
  isPopular?: boolean;
}

export interface PricingGuidance {
  defaultPricingModel: 'fixed_plan' | 'usage_based';
  recommendedPopularPlanId: string;
  usageRole: string;
  usageGuidance: string;
}

export interface EnterprisePricingPackage {
  id: string;
  name: string;
  description: string;
  annualRangeInr: { min: number; max: number | null };
  unitPricing?: { label: string; perUnitInr: number };
  features?: string[];
}

export interface EnterprisePricingFramework {
  currency: string;
  billingModel: string;
  summary: string;
  packages: EnterprisePricingPackage[];
  addOns: Array<{
    id: string;
    name: string;
    oneTimeRangeInr?: { min: number; max: number };
    oneTimeInr?: number;
    creditableToAnnual?: boolean;
  }>;
  contactEmail: string;
}

export interface ChannelPricingModel {
  id: string;
  name: string;
  description: string;
  annualRangeInr?: { min: number; max: number };
  perUnitInr?: number;
  msmeMonthlyInr?: number;
}

export interface ChannelPricing {
  currency: string;
  summary: string;
  models: ChannelPricingModel[];
  contactEmail: string;
}

export interface PublicPricingData {
  currency: string;
  pricesExclusiveOfGst: boolean;
  gstRate?: number;
  moduleEnabled: boolean;
  informationalOnly: boolean;
  provider: string;
  paymentMethods: { upi: boolean; netBanking: boolean; cards: boolean };
  guidance?: PricingGuidance;
  fixedPlans: PublicFixedPlan[];
  usagePricing: {
    currency: string;
    baseAmountInr: number;
    typicalRangeInr: { min: number; max: number };
    starterFloorInr?: number;
    overagePerDocumentInr?: number;
    summary: string;
    factors: Array<{ key: string; label: string; description: string }>;
  };
  enterprisePricing?: EnterprisePricingFramework;
  channelPricing?: ChannelPricing;
  msmePricingContactOnly?: boolean;
  msmePricingContactEmail?: string;
  billingNote: string;
}

export const MSME_PRICING_CONTACT_EMAIL = 'contact@sustainow.in';

export const GST_RATE = catalogSnapshot.fallbackPublicPricing.gstRate ?? 0.18;

export const PLAN_LIMITS = catalogSnapshot.planLimits as Record<PlanTier, PlanLimits>;

export const MONTHLY_TIER_AMOUNTS_EXCL = catalogSnapshot.monthlyTierAmountsExcl as Record<
  Exclude<PlanTier, 'free'>,
  number
>;

export const FALLBACK_PUBLIC_PRICING: PublicPricingData =
  catalogSnapshot.fallbackPublicPricing as PublicPricingData;

export const PRICING_TEASER = {
  headline: 'MSME plans for carbon intelligence',
  subline:
    'Five tiers from Free to Scale with published list prices (excl. GST). Starter ₹1,999 · Core ₹3,499 · Growth ₹5,999 · Scale from ₹10,833/mo equivalent.',
  popularPlanId: 'msme_growth_monthly',
};

export const PLAN_FEATURE_HIGHLIGHTS: Record<string, string[]> = {
  free: [
    'Register and complete onboarding',
    'Limited carbon assessment',
    'Up to 25 documents per month',
    'Watermarked exports',
  ],
  starter: [
    'Company profile & operations baseline',
    'Document upload with automated extraction',
    'Scope 1–3 carbon assessment',
    'Operational dashboard',
  ],
  core: [
    'Everything in Starter',
    'Full Scope 1–3 reporting',
    'Higher document and user limits',
    'Multi-site baseline (2 sites)',
  ],
  growth: [
    'Everything in Core',
    'Compliance hub (India & export tracks)',
    'BRSR-oriented reporting exports',
    'Reduce and offset',
    'Links to government energy and MSME schemes',
  ],
  scale: [
    'Everything in Growth',
    'Highest workflow and document capacity',
    'Priority support for multi-site operations',
    'Annual billing for finance teams',
  ],
};

export const FEATURE_COMPARISON_ROWS: Array<{
  label: string;
  free: boolean | string;
  starter: boolean | string;
  core: boolean | string;
  growth: boolean | string;
  scale: boolean | string;
}> = [
  { label: 'Price', free: 'Free', starter: '₹1,999/mo', core: '₹3,499/mo', growth: '₹5,999/mo', scale: 'From ₹10,833/mo' },
  { label: 'Documents / month', free: '25', starter: '50', core: '100', growth: '200', scale: '1,000+' },
  { label: 'Document capture', free: true, starter: true, core: true, growth: true, scale: true },
  { label: 'Scope 1–3 assessment', free: 'Limited', starter: true, core: true, growth: true, scale: true },
  { label: 'Operational dashboard', free: false, starter: true, core: true, growth: true, scale: true },
  { label: 'Compliance hub', free: false, starter: false, core: false, growth: true, scale: true },
  { label: 'PDF disclosure reports', free: 'Watermarked', starter: 'Basic', core: 'Basic', growth: 'Full', scale: 'Full' },
  { label: 'Reduce and offset', free: false, starter: false, core: false, growth: true, scale: true },
  { label: 'Government scheme links', free: false, starter: false, core: false, growth: true, scale: true },
  { label: 'Carbon credits workspace', free: false, starter: false, core: false, growth: true, scale: true },
  { label: 'Multi-site / complex workflows', free: '1 site', starter: '1 site', core: '2 sites', growth: '3 sites', scale: 'Extended' },
];

export const PRICING_FAQ: Array<{ question: string; answer: string }> = [
  {
    question: 'Do I need to pay before I can use the platform?',
    answer:
      'No. The Free tier covers onboarding and limited usage. You can register, complete your profile, and explore core workflows before choosing a paid plan.',
  },
  {
    question: 'How do I get MSME plan pricing?',
    answer:
      'Email contact@sustainow.in with your expected document volume, number of sites and users, and whether you need compliance or green-finance features. We will share a quote for the right tier or a usage-based option.',
  },
  {
    question: 'Which is better: fixed tier plans or usage-based pricing?',
    answer:
      'Most MSMEs should pick a fixed tier (Free through Scale) for predictable costs. Usage-based quotes are available for seasonal businesses or document overage beyond your tier limits—ask us when you request pricing.',
  },
  {
    question: 'What is the difference between monthly and annual billing?',
    answer:
      'They are the same tier features—only the billing cycle changes. Annual Starter, Core, and Growth plans are billed once per year. Scale is annual-only. Contact us for current billing options.',
  },
  {
    question: 'Are prices inclusive of GST?',
    answer:
      'Quotes are provided exclusive of 18% GST unless stated otherwise. Your invoice will reflect applicable taxes at checkout.',
  },
  {
    question: 'Is this a recurring subscription?',
    answer:
      'Plans are billed on a monthly or yearly cycle. Checkout uses one-time orders today; renewals are completed in-app or via invoice until automated subscription billing is enabled.',
  },
  {
    question: 'What happened to the Enterprise MSME plan?',
    answer:
      'The former "Enterprise" MSME annual tier is now called Scale to avoid confusion with corporate Enterprise accounts, which use custom programme pricing. Email contact@sustainow.in for Scale pricing.',
  },
  {
    question: 'How does corporate Enterprise pricing work?',
    answer:
      'Corporations and multi-entity programmes use annual platform fees plus optional per-supplier pricing. Contact Sustainow for a tailored Enterprise proposal.',
  },
];

export const formatInr = (value: number, currency = 'INR'): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    value
  );

export const formatInrRange = (
  min: number,
  max: number | null,
  currency = 'INR'
): string => {
  if (max == null) {
    return `${formatInr(min, currency)}+`;
  }
  return `${formatInr(min, currency)} – ${formatInr(max, currency)}`;
};

export const TIER_DISPLAY_ORDER: PlanTier[] = ['free', 'starter', 'core', 'growth', 'scale'];

export const TIER_DISPLAY_NAMES: Record<PlanTier, string> = {
  free: 'Free',
  starter: 'Starter',
  core: 'Core',
  growth: 'Growth',
  scale: 'Scale',
};

export const planTierKey = (plan: PublicFixedPlan): PlanTier =>
  plan.tier ?? resolvePlanTierKey(plan.planId);

/** One card per tier for the selected billing interval (Scale is annual-only). */
export const pickPlanForTier = (
  plans: PublicFixedPlan[],
  tier: PlanTier,
  interval: BillingInterval
): PublicFixedPlan | null => {
  const forTier = plans.filter((p) => planTierKey(p) === tier);
  if (forTier.length === 0) {
    return null;
  }
  if (tier === 'free') {
    return forTier.find((p) => planTierKey(p) === 'free') ?? forTier[0];
  }
  if (tier === 'scale') {
    return forTier.find((p) => p.interval === 'yearly') ?? forTier[0];
  }
  return forTier.find((p) => p.interval === interval) ?? null;
};

export const plansForBillingInterval = (
  plans: PublicFixedPlan[],
  interval: BillingInterval
): PublicFixedPlan[] =>
  TIER_DISPLAY_ORDER.map((tier) => pickPlanForTier(plans, tier, interval)).filter(
    (plan): plan is PublicFixedPlan => plan != null
  );

/** Approximate savings vs paying monthly for 12 months (null if not comparable). */
export const annualSavingsPercent = (
  plans: PublicFixedPlan[],
  tier: PlanTier
): number | null => {
  if (tier === 'free' || tier === 'scale') {
    return null;
  }
  const monthly = pickPlanForTier(plans, tier, 'monthly');
  const yearly = pickPlanForTier(plans, tier, 'yearly');
  if (!monthly?.amountInr || !yearly?.amountInr) {
    return null;
  }
  const fullYearMonthly = monthly.amountInr * 12;
  if (fullYearMonthly <= yearly.amountInr) {
    return null;
  }
  return Math.floor(((fullYearMonthly - yearly.amountInr) / fullYearMonthly) * 100);
};

export const resolvePlanTierKey = (planId: string): PlanTier => {
  if (planId.includes('free')) {
    return 'free';
  }
  if (planId.includes('scale') || planId.includes('enterprise')) {
    return 'scale';
  }
  if (planId.includes('growth')) {
    return 'growth';
  }
  if (planId.includes('core')) {
    return 'core';
  }
  return 'starter';
};

export const lowestMonthlyPlanInr = (plans: PublicFixedPlan[]): number => {
  const monthly = plans
    .filter((p) => p.interval === 'monthly' && (p.requiresPayment !== false) && p.amountInr > 0)
    .map((p) => p.amountInr);
  if (monthly.length === 0) {
    return 1999;
  }
  return Math.min(...monthly);
};

export const formatPlanLimitsLine = (limits?: PlanLimits): string | null => {
  if (!limits) {
    return null;
  }
  const sites = limits.sites == null ? 'Unlimited sites' : `${limits.sites} site${limits.sites === 1 ? '' : 's'}`;
  return `${sites} · ${limits.users} user${limits.users === 1 ? '' : 's'} · ${limits.documentsPerMonth} docs/mo`;
};
